import { useState, useEffect } from 'react';

type CookiePreferences = {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
};

const COOKIE_KEY = 'ge_cookie_consent';

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const saved = localStorage.getItem(COOKIE_KEY);
    if (!saved) {
      const timer = setTimeout(() => setVisible(true), 600);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveConsent = (prefs: CookiePreferences) => {
    localStorage.setItem(COOKIE_KEY, JSON.stringify({ ...prefs, date: new Date().toISOString() }));
    setVisible(false);
  };

  const handleAcceptAll = () => {
    const all: CookiePreferences = { necessary: true, analytics: true, marketing: true };
    setPreferences(all);
    saveConsent(all);
  };

  const handleRejectAll = () => {
    const minimal: CookiePreferences = { necessary: true, analytics: false, marketing: false };
    setPreferences(minimal);
    saveConsent(minimal);
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
  };

  const togglePref = (key: keyof CookiePreferences) => {
    if (key === 'necessary') return;
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-end justify-center pointer-events-none">
      <div
        className="w-full pointer-events-auto"
        style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.4) 0%, transparent 100%)' }}
      >
        <div className="max-w-5xl mx-auto px-4 pb-6">
          <div className="bg-white rounded-2xl overflow-hidden" style={{ boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}>
            {!showDetails ? (
              <div className="p-6 md:p-8">
                <div className="flex flex-col md:flex-row md:items-start gap-6">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-9 h-9 flex items-center justify-center bg-emerald-100 rounded-lg flex-shrink-0">
                        <i className="ri-shield-check-line text-lg text-emerald-600"></i>
                      </div>
                      <h3 className="text-base font-bold text-gray-900">Utilizziamo i cookie</h3>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Utilizziamo cookie tecnici necessari al funzionamento del sito e, previo tuo consenso, cookie analitici e di marketing per migliorare la tua esperienza e proporti contenuti personalizzati. Puoi accettare tutti i cookie, rifiutare quelli non necessari o personalizzare le tue preferenze.
                    </p>
                    <button
                      onClick={() => setShowDetails(true)}
                      className="mt-2 text-xs text-emerald-600 hover:text-emerald-700 underline cursor-pointer"
                    >
                      Personalizza preferenze
                    </button>
                  </div>
                  <div className="flex flex-col sm:flex-row md:flex-col gap-2 md:min-w-[180px]">
                    <button
                      onClick={handleAcceptAll}
                      className="px-5 py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Accetta tutti
                    </button>
                    <button
                      onClick={handleRejectAll}
                      className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Rifiuta non necessari
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-6 md:p-8">
                <div className="flex items-center gap-3 mb-5">
                  <button
                    onClick={() => setShowDetails(false)}
                    className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-left-line text-gray-500"></i>
                  </button>
                  <h3 className="text-base font-bold text-gray-900">Personalizza le preferenze cookie</h3>
                </div>

                <div className="space-y-4 mb-6">
                  <CookieToggleRow
                    title="Cookie necessari"
                    description="Indispensabili per il corretto funzionamento del sito. Non possono essere disattivati."
                    enabled={true}
                    locked
                    onToggle={() => {}}
                  />
                  <CookieToggleRow
                    title="Cookie analitici"
                    description="Ci aiutano a capire come gli utenti interagiscono con il sito, raccogliendo informazioni in forma anonima."
                    enabled={preferences.analytics}
                    onToggle={() => togglePref('analytics')}
                  />
                  <CookieToggleRow
                    title="Cookie di marketing"
                    description="Utilizzati per mostrare annunci pertinenti e misurare l'efficacia delle campagne pubblicitarie."
                    enabled={preferences.marketing}
                    onToggle={() => togglePref('marketing')}
                  />
                </div>

                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    onClick={handleSavePreferences}
                    className="flex-1 px-5 py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Salva preferenze
                  </button>
                  <button
                    onClick={handleAcceptAll}
                    className="flex-1 px-5 py-2.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Accetta tutti
                  </button>
                  <button
                    onClick={handleRejectAll}
                    className="flex-1 px-5 py-2.5 border border-gray-200 text-gray-500 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Rifiuta non necessari
                  </button>
                </div>
              </div>
            )}

            <div className="px-6 md:px-8 py-3 bg-gray-50 border-t border-gray-100">
              <p className="text-xs text-gray-400">
                Per maggiori informazioni consulta la nostra{' '}
                <button
                  onClick={() => setShowDetails(true)}
                  className="text-emerald-600 hover:underline cursor-pointer"
                >
                  Cookie Policy
                </button>
                {' '}e la{' '}
                <span className="text-emerald-600">Privacy Policy</span>.
                Puoi modificare le tue preferenze in qualsiasi momento.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface CookieToggleRowProps {
  title: string;
  description: string;
  enabled: boolean;
  locked?: boolean;
  onToggle: () => void;
}

function CookieToggleRow({ title, description, enabled, locked, onToggle }: CookieToggleRowProps) {
  return (
    <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm font-semibold text-gray-900">{title}</span>
          {locked && (
            <span className="text-xs px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full font-medium">Sempre attivo</span>
          )}
        </div>
        <p className="text-xs text-gray-500 leading-relaxed">{description}</p>
      </div>
      <button
        type="button"
        onClick={onToggle}
        disabled={locked}
        className={`relative flex-shrink-0 w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none mt-0.5 ${
          locked ? 'cursor-not-allowed' : 'cursor-pointer'
        } ${enabled ? 'bg-emerald-500' : 'bg-gray-300'}`}
        aria-pressed={enabled}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform duration-200 ${
            enabled ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  );
}
